import React from 'react';
import { motion } from 'framer-motion';
import { Download, Video, Play, ExternalLink } from 'lucide-react';

export const VideoCard = ({ title, url, description, creator, compact = false }) => {
  const getLoomEmbedUrl = (url) => {
    const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (match) {
      return `https://www.loom.com/embed/${match[1]}`;
    }
    return url;
  };
  const isLoom = /loom\.com\/share\//.test(url);

  if (compact) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-500 h-full flex flex-col"
      >
        <div className="p-5 flex flex-col h-full">
          <div className="mb-3 flex-shrink-0">
            <h4 className="font-semibold text-gray-900 text-base whitespace-normal leading-tight mb-1 min-h-[2.5rem] flex items-start">{title}</h4>
            {creator && <p className="text-sm text-gray-500 leading-tight">{creator}</p>}
          </div>
          <div className="relative w-full flex-grow">
            {isLoom ? (
              <iframe
                src={getLoomEmbedUrl(url)}
                frameBorder="0"
                allowFullScreen
                className="w-full h-48 rounded-xl shadow-sm"
                title={title}
              ></iframe>
            ) : (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full h-48 border border-gray-200 rounded-xl bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                title={title}
              >
                Open video
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-xl hover:shadow-gray-900/5 transition-all duration-500"
    >
      <div className="p-8">
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-900 whitespace-normal leading-tight mb-1">{title}</h4>
          {creator && <p className="text-gray-600 font-medium">{creator}</p>}
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>
        <div className="relative w-full">
          {isLoom ? (
            <iframe
              src={getLoomEmbedUrl(url)}
              frameBorder="0"
              allowFullScreen
              className="w-full h-80 rounded-2xl shadow-lg border border-slate-200/50"
              title={title}
            ></iframe>
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-800 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300"
            >
              Open video
              <ExternalLink className="w-4 h-4 ml-2 opacity-75" />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const VideoGrid = ({ videos, columns = 3 }) => {
  const gridClass = columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-1';
  
  return (
    <div className={`grid gap-6 ${gridClass} items-stretch`}>
      {videos.map((video, index) => (
        <VideoCard 
          key={index}
          title={video.name || video.title}
          url={video.url}
          creator={video.creator}
          compact={true}
        />
      ))}
    </div>
  );
};

export const StepCard = ({ icon: Icon, title, children, color = "[#02B491]", gradient = false }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 }}
    className="rounded-3xl border border-gray-200 p-8 bg-white transition-all duration-500 hover:shadow-xl hover:shadow-gray-900/5"
  >
    <div className="mb-8">
      <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
    </div>
    <div className="space-y-6">
      {children}
    </div>
  </motion.div>
);

export const InfoCard = ({ icon: Icon, title, description, accent = false, type = "info", children }) => {
  const getCardStyles = () => {
    switch (type) {
      case "success":
        return "border-emerald-200 bg-white";
      case "warning":
        return "border-amber-200 bg-white";
      case "tip":
        return "border-blue-200 bg-white";
      default:
        return accent 
          ? 'border-[#02B491]/30 bg-white' 
          : 'border-gray-200 bg-white';
    }
  };

  const getIconStyles = () => {
    switch (type) {
      case "success":
        return "bg-emerald-500 shadow-emerald-500/20";
      case "warning":
        return "bg-amber-500 shadow-amber-500/20";
      case "tip":
        return "bg-blue-500 shadow-blue-500/20";
      default:
        return accent ? 'bg-[#02B491] shadow-emerald-500/20' : 'bg-slate-400 shadow-slate-400/20';
    }
  };

  return (
    <div className={`p-6 rounded-2xl border ${getCardStyles()} transition-all duration-300 hover:shadow-lg hover:shadow-gray-900/5`}>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
        {description && <p className="text-gray-600 leading-relaxed">{description}</p>}
        {children}
      </div>
    </div>
  );
};

export const ExampleCard = ({ emoji, title, downloadText, children, links = [] }) => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300"
  >
    <div className="mb-4">
      <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
    </div>
    
    {links.length > 0 && (
      <div className="flex flex-wrap gap-3 mb-4">
        {links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-md ${link.className || 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {link.icon && <link.icon className="w-4 h-4 mr-2" />}
            {link.text}
            <ExternalLink className="w-3 h-3 ml-2 opacity-60" />
          </a>
        ))}
      </div>
    )}
    
    {downloadText && (
      <div className="mb-4">
        <span className="inline-flex items-center text-sm text-[#02B491] hover:text-emerald-600 font-medium cursor-pointer transition-colors">
          <Download className="w-4 h-4 mr-2" />
          {downloadText}
        </span>
      </div>
    )}
    {children}
  </motion.div>
);
